import { Component, Fragment, useEffect, useMemo, useRef, useState, type ErrorInfo, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search, Filter, Download, ChevronRight, ChevronDown, AlertTriangle, ShieldCheck, Clock, User, Loader2, GitBranch, ShieldAlert, Activity as ActivityIcon, Layers, CheckSquare, Square, X, CheckCircle2, EyeOff } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { DecisionHeader, type DecisionAction } from '@/components/viz/DecisionHeader';
import { VirtualList } from '@/components/ui/virtual-list';
import { PageGuide } from '@/components/ui/page-guide';
import { Tabs, TabsList, TabsTrigger, TabsContent, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Separator } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { riskScore, byRiskDesc, dedupeTopBy, type RiskBand, type RiskScore } from '@/lib/risk-score';
import { listDiscoveryFindings, patchDiscoveryFindingStatus, remediateFinding, getFindingLineage, getDiscoveryRiskRanking, type DiscoveryFinding, type FindingLineage } from '@/lib/api';
import { FocttaCoreBadge } from '@/components/foctta-core-badge';
import { useSseStream } from '@/lib/useSseStream';
import { LiveBadge } from '@/components/ui/live-badge';
import { useNavigate } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/primitives';
import { GitBranch as GitBranchIcon, Database as DatabaseIcon, Radar as RadarIcon, Download as DownloadIcon, Info as InfoIcon } from 'lucide-react';

type Severity = 'critical' | 'high' | 'medium' | 'low';
type Status = 'open' | 'acknowledged' | 'in_remediation' | 'resolved' | 'false_positive';

interface BreachColumn {
  columnName: string;
  matchedClasses: string[];
  matchesFound: number;
}

export interface Finding {
  id: string;
  type: string;
  severity: Severity;
  status: Status;
  asset: string;
  pii: string[];
  detected: string;
  owner: string;
  /** Real distinct-row count; null when the scan didn't capture one (name-only). */
  count: number | null;
  // Breach-DSPM forensics (present when this finding answers a Core breach).
  isBreach: boolean;
  incidentRef: string | null;
  systemIp: string | null;
  sourceSystem: string | null;
  /** 'review' | 'orphan_no_owner' | 'unauthorized' */
  legitimacy: string | null;
  matchedColumns: BreachColumn[];
  /** Privacy-safe masked fragments of the actual matched values (in-pod masked). */
  maskedSamples: string[];
  /** Classifier match ratio: matchedCount of samplesCount sampled values matched. */
  matchedCount: number | null;
  samplesCount: number | null;
  /** Peak classifier confidence 0..1. */
  confidence: number;
  /** Evidence/validation methods, e.g. ['regex','checksum']. */
  evidence: string[];
  /** Deep-scan forensic sweep (present on deep-scan breach findings). */
  toxicCombinations: string[][];
  recentAccess: Array<{ actor: string; lastAccessedAt: string; events: number }>;
  activityObserved: boolean;
  /** doc-32 confidence tier. 'review' = needs a human glance; kept out of the main list. */
  tier: 'confirmed' | 'review';
  /**
   * Plain-English reason for the tier, emitted by the scanner alongside it
   * (2026-07-21). Null for findings produced before that, which is why it is
   * rendered conditionally rather than with a placeholder - "no reason
   * recorded" is the honest state for older findings, not an empty string.
   */
  rationale: string | null;
  /** DPDPA class: children / sensitive_financial / sensitive_health / personal_identifier / personal / credential / device. */
  governanceCategory: string;
  /** high | medium | low - drives the sensitivity badge. */
  sensitivity: string;
  // ── Malware / threat-signature fields (present when this finding is a
  //    malicious_file_detected, or metadata.detection includes yara/hash).
  //    These drive the MALWARE drawer presentation instead of the PII verdict. ──
  /** True when this finding is a malware / threat-signature match, not PII. */
  isMalware: boolean;
  /** Malware family, e.g. "PHP.WebShell" / "Cobalt.Strike". */
  malwareFamily: string | null;
  /** Name of the YARA rule that matched in-pod. */
  yaraRule: string | null;
  /** Full SHA-256 of the matched file (threat-intel hash match). */
  sha256: string | null;
  /** File path the malware was found at. */
  filePath: string | null;
  /** Sensitive data co-located with the malware (raises exfil priority). */
  coLocatedSensitive: string | null;
  /** Detection method string, e.g. "yara+hash". */
  detection: string | null;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

interface ForensicMeta {
  currentOwner?: string | null;
  systemIp?: string | null;
  sourceSystem?: string | null;
  recordCount?: number;
  matchedColumns?: BreachColumn[];
  access?: { legitimacy?: string };
  deepSweep?: {
    toxicCombinations?: string[][];
    recentAccess?: Array<{ actor?: string; lastAccessedAt?: string; events?: number }>;
    activityObserved?: boolean;
  };
}

/** Coerce any value to a safe display string; objects / null fall back to `fallback`. */
function safeStr(v: unknown, fallback = ''): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return fallback;
}

// Exported for unit tests: proves the adapter is bulletproof against malformed
// real-scan DTOs (the "blur, no detail panel" repro). A field that throws in
// BOTH the detail view and its safety fallback would propagate past the error
// boundary and unmount the whole dialog, leaving only the blurred overlay.
// See __tests__/findings-adapt.test.ts.
export function adaptDtoToFinding(f: DiscoveryFinding): Finding {
  const sev: Severity =
    f.severity === 'critical' || f.severity === 'high' || f.severity === 'medium' || f.severity === 'low'
      ? f.severity
      : 'medium';
  const stat: Status =
    f.status === 'open' || f.status === 'resolved' || f.status === 'false_positive'
      ? (f.status as Status)
      : f.status === 'in_progress'
        ? 'in_remediation'
        : f.status === 'acknowledged'
          ? 'acknowledged'
          : 'open';
  // metadata is on the wire (not in the DTO type) - read it defensively. The
  // breach executor stamps { forensic, assetExternalRef, incidentRef }.
  const meta =
    (f as unknown as { metadata?: Record<string, unknown> }).metadata ?? {};
  const forensic = (meta.forensic as ForensicMeta | undefined) ?? {};
  // Real-scan findings can carry null / odd fields the seeded demo never has.
  // Coerce every field the views touch to a safe shape here so neither the
  // detail view nor its fallback can throw on data. (2026-06-22 blur fix)
  const classifications = Array.isArray(f.classifications) ? f.classifications : [];
  const safeType =
    typeof f.findingType === 'string' && f.findingType.length > 0 ? f.findingType : 'finding';
  const isBreach = safeType === 'breach_data_located' || forensic.sourceSystem != null;
  const owner =
    typeof forensic.currentOwner === 'string' && forensic.currentOwner.length > 0
      ? forensic.currentOwner
      : 'unassigned';
  // Malware / threat-signature fields. Detection string ("yara+hash") or the
  // malicious_file_detected type flip this finding into the MALWARE drawer.
  const detectionStr = typeof meta.detection === 'string' ? meta.detection : null;
  const isMalware =
    safeType === 'malicious_file_detected' ||
    (typeof detectionStr === 'string' &&
      (detectionStr.includes('yara') || detectionStr.includes('hash')));
  const coLoc = meta.coLocatedSensitive;
  const coLocatedSensitive = Array.isArray(coLoc)
    ? coLoc.filter((x): x is string => typeof x === 'string').join(', ') || null
    : typeof coLoc === 'string' && coLoc.length > 0
      ? coLoc
      : typeof coLoc === 'number' || typeof coLoc === 'boolean'
        ? String(coLoc)
        : null;
  return {
    id: typeof f.id === 'string' ? f.id : String(f.id ?? ''),
    type: safeType,
    severity: sev,
    status: stat,
    asset: safeStr(
      (meta.assetExternalRef as unknown) ?? forensic.sourceSystem ?? safeType,
      safeType,
    ),
    pii: classifications
      .map((c) => c.label)
      .filter((l): l is string => typeof l === 'string' && l.length > 0),
    detected: timeAgo(f.createdAt),
    owner,
    // Real distinct-row count when the scan captured one; null (→ "-") otherwise,
    // so name-only column findings never show a misleading "1 row".
    count: typeof forensic.recordCount === 'number' && forensic.recordCount > 0 ? forensic.recordCount : null,
    isBreach,
    incidentRef: (meta.incidentRef as string | null | undefined) ?? null,
    systemIp: forensic.systemIp ?? null,
    sourceSystem: forensic.sourceSystem ?? null,
    legitimacy: forensic.access?.legitimacy ?? null,
    matchedColumns: Array.isArray(forensic.matchedColumns)
      ? forensic.matchedColumns.map((c) => ({
          columnName: safeStr((c as BreachColumn)?.columnName),
          matchedClasses: Array.isArray((c as BreachColumn)?.matchedClasses)
            ? (c as BreachColumn).matchedClasses.filter((x): x is string => typeof x === 'string')
            : [],
          matchesFound:
            typeof (c as BreachColumn)?.matchesFound === 'number' ? (c as BreachColumn).matchesFound : 0,
        }))
      : [],
    maskedSamples: Array.isArray(meta.masked_samples)
      ? (meta.masked_samples as unknown[]).filter((s): s is string => typeof s === 'string')
      : [],
    matchedCount: typeof meta.matched_count === 'number' ? meta.matched_count : null,
    samplesCount: typeof meta.samples_count === 'number' ? meta.samples_count : null,
    confidence: typeof f.confidence === 'number' ? f.confidence : (classifications[0]?.confidence ?? 0),
    evidence: classifications
      .map((c) => c.label)
      .filter((l): l is string => typeof l === 'string'),
    toxicCombinations: Array.isArray(forensic.deepSweep?.toxicCombinations)
      ? forensic.deepSweep!.toxicCombinations!.filter((c): c is string[] => Array.isArray(c))
      : [],
    recentAccess: Array.isArray(forensic.deepSweep?.recentAccess)
      ? forensic.deepSweep!.recentAccess!.map((r) => ({
          actor: String(r.actor ?? 'unknown'),
          lastAccessedAt: String(r.lastAccessedAt ?? ''),
          events: typeof r.events === 'number' ? r.events : 0,
        }))
      : [],
    activityObserved: forensic.deepSweep?.activityObserved === true,
    tier: (meta.tier as string | undefined) === 'review' ? 'review' : 'confirmed',
    rationale: typeof meta.rationale === 'string' && meta.rationale.length > 0 ? meta.rationale : null,
    // DUAL-READ (2026-08-04). New findings carry `governance_category`; rows
    // written before the rename carry `dpdpa_category`, and the Rust pod keeps
    // emitting the old key until it next deploys. Reading both means the rename
    // needs no flag-day and an un-migrated tenant DB still renders.
    governanceCategory:
      typeof meta.governance_category === 'string'
        ? meta.governance_category
        : typeof meta.dpdpa_category === 'string'
          ? meta.dpdpa_category
          : 'personal',
    sensitivity: typeof meta.sensitivity === 'string' ? meta.sensitivity : 'medium',
    isMalware,
    malwareFamily: typeof meta.malwareFamily === 'string' ? meta.malwareFamily : null,
    yaraRule: typeof meta.yaraRule === 'string' ? meta.yaraRule : null,
    sha256: typeof meta.sha256 === 'string' ? meta.sha256 : null,
    filePath: typeof meta.filePath === 'string' ? meta.filePath : null,
    coLocatedSensitive,
    detection: detectionStr,
  };
}

/** Human validation note per PII type - what makes the match credible. Specific
 * on purpose: this is the basis the DPO trusts instead of checking each row. */
function validationNote(pii: string[]): string {
  if (pii.includes('aadhaar')) return '12 digits, first digit 2-9, valid UIDAI Verhoeff checksum';
  if (pii.includes('pan')) return 'PAN format with a valid holder-type letter';
  if (pii.includes('credit_card')) return 'issuer BIN range + valid Luhn checksum';
  if (pii.includes('imei')) return '15 digits + valid Luhn, in a device column';
  if (pii.includes('gstin')) return 'state code + embedded PAN + GST checksum';
  if (pii.includes('ifsc')) return 'IFSC bank / branch code format';
  if (pii.includes('phone')) return 'Indian mobile number format';
  if (pii.includes('email')) return 'valid email address (local@domain.tld)';
  if (pii.some((p) => ['aws_access_key', 'private_key', 'jwt_token', 'db_connection_string', 'api_key'].includes(p)))
    return 'known credential / secret format';
  return 'structural pattern match';
}

// ── doc 33: grouped, risk-ranked triage queue machinery ──────────────────
// Findings are scored once, grouped (by asset / type / DPDPA class / owner),
// and the groups + rows are flattened into one windowed list so thousands of
// findings stay smooth and a hard "top N" cap is never needed.

export type GroupBy = 'asset' | 'type' | 'dpdpa' | 'owner' | 'none';
export type ScoredFinding = Finding & { risk: RiskScore };

export interface FindingGroup {
  key: string;
  label: string;
  findings: ScoredFinding[];
  band: RiskBand;
  topScore: number;
  /** Sum of known record counts across the group. */
  records: number;
}

type RenderItem =
  | { kind: 'group'; group: FindingGroup }
  | { kind: 'finding'; finding: ScoredFinding; isLastInGroup: boolean };

const GROUP_ROW_H = 48;
const FINDING_ROW_H = 70;

const GROUP_BY_OPTIONS: Array<{ id: GroupBy; label: string }> = [
  { id: 'asset', label: 'Asset' },
  { id: 'type', label: 'Type' },
  { id: 'dpdpa', label: 'DPDPA class' },
  { id: 'owner', label: 'Owner' },
  { id: 'none', label: 'No grouping' },
];

const GROUP_NOUN: Record<GroupBy, string> = {
  asset: 'assets',
  type: 'finding types',
  dpdpa: 'DPDPA classes',
  owner: 'owners',
  none: 'findings',
};

function groupKeyOf(f: Finding, by: GroupBy): string {
  switch (by) {
    case 'asset': return f.asset || 'unknown';
    case 'type': return f.type || 'finding';
    case 'dpdpa': return f.governanceCategory || 'personal';
    case 'owner': return f.owner || 'unassigned';
    case 'none': return '__all__';
  }
}

function groupLabelOf(key: string, by: GroupBy): string {
  if (by === 'dpdpa' || by === 'type') return key.replace(/_/g, ' ');
  return key;
}

/** Build risk-ranked groups: groups ordered by worst risk, rows by risk desc.
 *  Exported for unit tests (groups-build.test.ts). */
export function buildGroups(findings: ScoredFinding[], by: GroupBy): FindingGroup[] {
  const map = new Map<string, ScoredFinding[]>();
  for (const f of findings) {
    const k = groupKeyOf(f, by);
    const arr = map.get(k);
    if (arr) arr.push(f);
    else map.set(k, [f]);
  }
  const groups: FindingGroup[] = [];
  for (const [key, arr] of map) {
    arr.sort((a, b) => b.risk.score - a.risk.score);
    const records = arr.reduce((s, f) => s + (typeof f.count === 'number' ? f.count : 0), 0);
    groups.push({
      key,
      label: groupLabelOf(key, by),
      findings: arr,
      band: arr[0]?.risk.band ?? 'low',
      topScore: arr[0]?.risk.score ?? 0,
      records,
    });
  }
  groups.sort((a, b) => b.topScore - a.topScore);
  return groups;
}

// NOTE: the previous version of this file held a `SAMPLE_FALLBACK: Finding[]`
// constant that was silently swapped in on any fetch error. It was removed
// in the Phase-2 hardening pass - operators would have been triaging
// fabricated records. Errors now surface as an actionable retry CTA; the
// table renders empty until the live response lands.

/**
 * Export the current visible (filtered) findings as a CSV file.
 * Triggered by the toolbar "Export" button. Stays client-side: no PII
 * leaves the browser, the CSV is generated from data the user can already
 * see on screen.
 */
function exportFindingsAsCsv(rows: Finding[]) {
  if (rows.length === 0) return;
  const header = ['id', 'type', 'severity', 'status', 'asset', 'pii', 'detected', 'owner', 'count'];
  const escape = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows
    .map((r) =>
      [r.id, r.type, r.severity, r.status, r.asset, r.pii.join('|'), r.detected, r.owner, r.count ?? '']
        .map(escape)
        .join(','),
    )
    .join('\n');
  const csv = `${header.join(',')}\n${body}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `discovery-findings-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Error boundary for the finding detail dialog. A render crash in any
 * sub-section (lineage, a badge, a stat) must NEVER blank the whole dialog -
 * inside the federated remote that blanks the page (the "blur with no
 * details" symptom). On a crash it shows the finding's core info and logs the
 * real error to the console for root-cause diagnosis.
 */
class DetailErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[Findings] detail render crashed:', error, info.componentStack);
  }
  render(): ReactNode {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/** Always-safe core detail view - the boundary fallback. */
function FindingDetailCore({ finding }: { finding: Finding }) {
  return (
    <div className="space-y-3">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 capitalize">
          <SeverityDot severity={finding.severity} />
          {finding.type.replace(/_/g, ' ')}
        </DialogTitle>
        <DialogDescription>
          <code className="font-mono text-xs break-all">{finding.asset}</code>
        </DialogDescription>
      </DialogHeader>
      <Separator />
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Severity" value={finding.severity} />
        <Stat label="PII categories" value={finding.pii.join(', ') || '-'} />
        <Stat label="Confidence" value={`${Math.round((finding.confidence || 0) * 100)}%`} />
        <Stat label="Detected" value={finding.detected} />
      </div>
      {finding.maskedSamples.length > 0 && (
        <div className="glass-lite rounded-md p-3 border border-emerald-500/20">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            What we found (masked samples)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {finding.maskedSamples.map((s) => (
              <code key={s} className="text-xs text-emerald-300/90 bg-emerald-500/10 rounded px-1.5 py-0.5 font-mono">
                {s}
              </code>
            ))}
          </div>
        </div>
      )}
      <p className="text-xs text-muted-foreground">Core finding shown; some detail sections were unavailable.</p>
    </div>
  );
}

export function DiscoveryFindings() {
  const [filter, setFilter] = useState<'all' | Severity>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Finding | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  /** Headline posture = canonical backend asset-level /risk-ranking score (same
   *  number as the Dashboard + the board-summary Core ingests). The finding-level
   *  scores below stay only for ordering / pills on the rows. */
  const [assetPosture, setAssetPosture] = useState<{ score: number; band: RiskBand }>({ score: 0, band: 'low' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Map of finding id → pending status mutation. Drives the per-row spinner. */
  const [pendingMutations, setPendingMutations] = useState<Set<string>>(new Set());
  /** Toggle visibility of the secondary filter row in the header. */
  const [showFilters, setShowFilters] = useState(false);
  /** Review lane (doc 32). When true the list shows ONLY 'review'-tier findings;
   * default shows only 'confirmed', keeping uncertain matches out of the main list. */
  const [reviewView, setReviewView] = useState(false);
  /** doc 33 - grouped triage queue. Group the list so thousands of findings
   * collapse into a scannable set of buckets, each with a count + worst risk +
   * a bulk action, instead of a flat infinite scroll. */
  const [groupBy, setGroupBy] = useState<GroupBy>('asset');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  /** Bulk-triage selection (doc 33 #4). Distinct from `selected` (detail target). */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /**
   * P1 OBS-2.alt (2026-05-26) - visible pagination. pageSize drives
   * the limit on each fetch; cursorStack is the trail of nextCursor
   * tokens we've consumed so Back can rewind one page. nextCursor is
   * the server-supplied marker for the *next* page (null = end).
   */
  const PAGE_SIZE_CHOICES = [20, 50, 100, 200] as const;
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_CHOICES)[number]>(50);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  // Wave 5 Group-A (2026-05-27) - live SSE tail of new discovery
  // findings. Backend at /v1/discovery/findings/stream has been ready
  // for weeks; this page never subscribed. New-event count surfaces as
  // a chip in the page header - operators can refresh to fold the
  // new rows into the paginated table.
  const {
    events: liveEvents,
    connected: sseConnected,
    paused: ssePaused,
    setPaused: setSsePaused,
  } = useSseStream<DiscoveryFinding>({
    url: '/api/v1/discovery/findings/stream',
    bufferSize: 100,
    acceptEventName: (name) => name.startsWith('finding.'),
  });
  const liveNewCount = liveEvents.length;

  const refresh = (
    signal?: AbortSignal,
    opts: { cursor?: string; resetStack?: boolean } = {},
  ) =>
    listDiscoveryFindings({ limit: pageSize, cursor: opts.cursor })
      .then((res) => {
        if (signal?.aborted) return;
        const live = res.findings.map(adaptDtoToFinding);
        setFindings(live);
        setNextCursor(res.nextCursor);
        if (opts.resetStack) setCursorStack([]);
        setError(null);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        // 401 → api.ts already redirects to /login. For any other failure,
        // surface a clear error + retry CTA - never substitute synthetic
        // data, because operators would triage it as real.
        setFindings([]);
        setError(err instanceof Error ? err.message : 'Failed to load findings');
      });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal, { resetStack: true }).finally(() => setLoading(false));
    return () => ctrl.abort();
    // pageSize change should re-fetch from page 1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  // Headline posture from the canonical asset-level risk-ranking (once on mount).
  useEffect(() => {
    const RB: readonly string[] = ['critical', 'high', 'medium', 'low'];
    getDiscoveryRiskRanking()
      .then((r) => {
        const top = r.items[0];
        if (top) setAssetPosture({ score: top.score, band: (RB.includes(top.band) ? top.band : 'low') as RiskBand });
      })
      .catch(() => { /* leave default; the header still renders */ });
  }, []);

  // Dashboard → Findings handoff. The DSPM dashboard stashes a focus finding
  // id or a severity filter in sessionStorage, then navigates here; we honour
  // it once on arrival so "click a tile/row on the dashboard" lands on the
  // right thing. sessionStorage (not a URL search param) keeps this
  // router-config-free and robust across the federated remote boundary.
  const focusAppliedRef = useRef(false);
  useEffect(() => {
    try {
      const sev = sessionStorage.getItem('discovery.findingsSeverity');
      if (sev) {
        sessionStorage.removeItem('discovery.findingsSeverity');
        if (['critical', 'high', 'medium', 'low'].includes(sev)) setFilter(sev as Severity);
      }
    } catch { /* sessionStorage may be unavailable */ }
  }, []);
  useEffect(() => {
    if (focusAppliedRef.current || findings.length === 0) return;
    let id: string | null = null;
    try { id = sessionStorage.getItem('discovery.focusFinding'); } catch { /* ignore */ }
    if (!id) return;
    const match = findings.find((f) => f.id === id);
    if (match) {
      setSelected(match);
      focusAppliedRef.current = true;
      try { sessionStorage.removeItem('discovery.focusFinding'); } catch { /* ignore */ }
    }
  }, [findings]);

  const goNextPage = (): void => {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, nextCursor]);
    void refresh(undefined, { cursor: nextCursor });
  };
  const goPrevPage = (): void => {
    if (cursorStack.length === 0) return;
    const newStack = cursorStack.slice(0, -1);
    const prevCursor = newStack.length > 0 ? newStack[newStack.length - 1] : undefined;
    setCursorStack(newStack);
    void refresh(undefined, { cursor: prevCursor });
  };
  const pageIndex = cursorStack.length; // 0-based

  /**
   * Real button handler. Optimistically marks the row mid-mutation so the
   * UI is responsive; rolls back on error. On success, refreshes the list
   * to pick up any cascading changes (e.g. a "false positive" suppress
   * also clears related findings on the same asset).
   */
  const applyStatusChange = async (
    finding: Finding,
    serverStatus: DiscoveryFinding['status'],
    resolutionNote?: string,
  ) => {
    setPendingMutations((prev) => new Set(prev).add(finding.id));
    try {
      await patchDiscoveryFindingStatus(finding.id, serverStatus, resolutionNote);
      await refresh();
      // If the user was viewing this finding in the drawer, close it
      // so the new state is unambiguous.
      setSelected(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status update failed');
    } finally {
      setPendingMutations((prev) => {
        const next = new Set(prev);
        next.delete(finding.id);
        return next;
      });
    }
  };

  // ── Bulk-triage actions over the current selection (doc 33 #4). ──
  const clearSelection = (): void => setSelectedIds(new Set());
  const toggleSelect = (id: string): void =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleSelectGroup = (g: FindingGroup): void =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      const allSel = g.findings.every((f) => n.has(f.id));
      g.findings.forEach((f) => (allSel ? n.delete(f.id) : n.add(f.id)));
      return n;
    });
  const toggleCollapse = (key: string): void =>
    setCollapsedGroups((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const applyBulkStatusChange = async (
    serverStatus: DiscoveryFinding['status'],
    note: string,
  ): Promise<void> => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setPendingMutations((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => n.add(id));
      return n;
    });
    try {
      // Sequential so each carries its own idempotency key + audit entry.
      for (const id of ids) await patchDiscoveryFindingStatus(id, serverStatus, note);
      await refresh();
      clearSelection();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setPendingMutations((prev) => {
        const n = new Set(prev);
        ids.forEach((id) => n.delete(id));
        return n;
      });
    }
  };

  // ── Single-source derivation (doc 33 #2, #3): ONE computation feeds the
  // posture, the tabs, the groups, and the rows, so every count agrees. The
  // founder's "header 0 critical vs tab Critical(6) vs 10 total" mismatch is
  // eliminated because the tab counts sum exactly to the lane total shown. ──
  const derived = useMemo(() => {
    const scored: ScoredFinding[] = findings.map((f) => ({
      ...f,
      risk: riskScore({
        severity: f.severity,
        governanceCategory: f.governanceCategory,
        sensitivity: f.sensitivity,
        legitimacy: f.legitimacy,
        tier: f.tier,
        confidence: f.confidence,
        count: f.count,
        activityObserved: f.activityObserved,
        isBreach: f.isBreach,
        status: f.status,
      }),
    }));
    const reviewCount = scored.filter((f) => f.tier === 'review').length;
    // The lane the operator is looking at: confirmed (default) or review.
    const lane = scored.filter((f) => (reviewView ? f.tier === 'review' : f.tier !== 'review'));
    const bySeverity: Record<Severity, number> = {
      critical: lane.filter((f) => f.severity === 'critical').length,
      high: lane.filter((f) => f.severity === 'high').length,
      medium: lane.filter((f) => f.severity === 'medium').length,
      low: lane.filter((f) => f.severity === 'low').length,
    };
    // Rows actually rendered = lane filtered by the active tab / status / search.
    const q = search.trim().toLowerCase();
    const visible = lane
      .filter((f) => filter === 'all' || f.severity === filter)
      .filter((f) => statusFilter === 'all' || f.status === statusFilter)
      .filter(
        (f) =>
          !q ||
          `${f.asset} ${f.type} ${f.id} ${f.owner} ${f.pii.join(' ')}`.toLowerCase().includes(q),
      )
      .sort((a, b) => b.risk.score - a.risk.score);
    const open = lane.filter((f) => f.status !== 'resolved' && f.status !== 'false_positive');
    const openRanked = [...open].sort((a, b) => b.risk.score - a.risk.score);
    const posture: RiskScore = openRanked[0]?.risk ?? { score: 0, band: 'low', factors: [] };
    return { lane, reviewCount, bySeverity, visible, openRanked, posture, openCount: open.length };
  }, [findings, reviewView, filter, statusFilter, search]);

  const { lane, reviewCount, bySeverity, visible, openRanked, posture, openCount } = derived;

  const groups = useMemo(
    () => (groupBy === 'none' ? [] : buildGroups(visible, groupBy)),
    [visible, groupBy],
  );

  const renderItems = useMemo<RenderItem[]>(() => {
    if (groupBy === 'none') {
      return visible.map((f) => ({ kind: 'finding' as const, finding: f, isLastInGroup: true }));
    }
    const items: RenderItem[] = [];
    for (const g of groups) {
      items.push({ kind: 'group', group: g });
      if (!collapsedGroups.has(g.key)) {
        g.findings.forEach((f, idx) =>
          items.push({ kind: 'finding', finding: f, isLastInGroup: idx === g.findings.length - 1 }),
        );
      }
    }
    return items;
  }, [groups, visible, groupBy, collapsedGroups]);

  // The 3 highest-risk DISTINCT open exposures become the "do this next"
  // actions - rolled up by asset+type so we never show the same finding 3x.
  const decisionActions: DecisionAction[] = dedupeTopBy(
    openRanked,
    (f) => `${f.asset}::${f.type}`,
    3,
  ).map(({ item: f, count }) => ({
    id: f.id,
    label: `${f.type.replace(/_/g, ' ')} in ${f.asset}`,
    detail: [
      count > 1 ? `${count} findings` : null,
      f.count !== null ? `${f.count.toLocaleString('en-IN')} records` : null,
      f.owner === 'unassigned' ? 'no owner' : f.owner,
    ]
      .filter(Boolean)
      .join(' · '),
    band: f.risk.band,
    onClick: () => setSelected(f),
  }));
  // Count OPEN criticals/highs (not all-status) so "... open" is accurate and
  // matches the Dashboard's open-based posture summary (cross-page consistency).
  const openCrit = openRanked.filter((f) => f.severity === 'critical').length;
  const openHigh = openRanked.filter((f) => f.severity === 'high').length;
  const postureSummary =
    openCount === 0
      ? `No open exposures in this lane${reviewView ? '' : ' (confirmed findings)'}.`
      : `${openCrit} critical, ${openHigh} high open across ${groupBy === 'none' ? visible.length : groups.length} ${GROUP_NOUN[groupBy]}.`;

  // Virtualized list height: grows to fit small sets, caps so big sets scroll.
  const listViewportH = Math.min(
    640,
    Math.max(220, renderItems.reduce((h, it) => h + (it.kind === 'group' ? GROUP_ROW_H : FINDING_ROW_H), 0)),
  );

  return (
    <PageContainer>
      <PageHeader
        title="Findings"
        subtitle="Every PII exposure detected across connectors. Click any row to inspect lineage, fired rules, and remediation playbook."
        badges={[
          { label: `${lane.length} ${reviewView ? 'in review' : 'confirmed'}`, variant: 'glass' },
          ...(bySeverity.critical > 0
            ? [{ label: `${bySeverity.critical} critical`, variant: 'critical' as const }]
            : []),
          ...(liveNewCount > 0
            ? [{ label: `+${liveNewCount} new`, variant: 'success' as const }]
            : []),
        ]}
        actions={
          <>
            <LiveBadge
              status={ssePaused ? 'idle' : sseConnected ? 'live' : 'connecting'}
              label={ssePaused ? 'Live paused' : sseConnected ? 'Live' : 'Connecting'}
            />
            <Button
              variant="glass"
              size="sm"
              onClick={() => setSsePaused(!ssePaused)}
              title={ssePaused ? 'Resume live tail' : 'Pause live tail'}
            >
              {ssePaused ? 'Resume tail' : 'Pause tail'}
            </Button>
            <FocttaCoreBadge label="BU owner live" />
            <Button
              variant="glass"
              size="sm"
              onClick={() => setShowFilters((v) => !v)}
              aria-pressed={showFilters}
            >
              <Filter className="h-4 w-4" /> Filters
            </Button>
            <Button
              variant="glass"
              size="sm"
              onClick={() => exportFindingsAsCsv(visible)}
              disabled={visible.length === 0}
              title={visible.length === 0 ? 'No findings to export' : 'Download visible findings as CSV'}
            >
              <Download className="h-4 w-4" /> Export
            </Button>
          </>
        }
      />

      <PageGuide id="findings">
        Each finding is a place where sensitive data (like Aadhaar, PAN, or email) was found in your
        systems. The list is sorted so the riskiest sit on top. Click any row to see proof it is real
        and exactly what to do about it.
      </PageGuide>

      {/* Decision header (doc 33): worst open risk + the 3 things to do next. */}
      {!loading && lane.length > 0 && (
        <DecisionHeader
          score={assetPosture.score}
          band={assetPosture.band}
          scoreLabel="Data risk posture"
          actions={decisionActions}
          summary={postureSummary}
        />
      )}

      {/* Loading state (first paint only) */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading findings…
        </div>
      )}

      {/* Error banner - surfaces RFC 7807 detail from the API; offers Retry. */}
      {error && !loading && (
        <div className="glass-lite border border-sev-high/40 rounded-md p-3 mb-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
          <div className="flex-1 text-xs">
            <div className="font-medium">{error}</div>
            <div className="text-muted-foreground mt-0.5">
              The table below shows whatever loaded successfully before the failure (may be empty).
            </div>
          </div>
          <Button size="sm" variant="glass" onClick={() => { setError(null); setLoading(true); refresh().finally(() => setLoading(false)); }}>
            Retry
          </Button>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="glass-lite flex items-center gap-2 rounded-md px-3 py-1.5 flex-1 min-w-[260px] max-w-md">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search asset, finding type, or BLK request id..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground/70"
          />
        </div>

        {/* Tab counts come from `lane` and sum exactly to All - one source. */}
        <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | Severity)}>
          <TabsList>
            <TabsTrigger value="all">All ({lane.length})</TabsTrigger>
            <TabsTrigger value="critical">Critical ({bySeverity.critical})</TabsTrigger>
            <TabsTrigger value="high">High ({bySeverity.high})</TabsTrigger>
            <TabsTrigger value="medium">Medium ({bySeverity.medium})</TabsTrigger>
            <TabsTrigger value="low">Low ({bySeverity.low})</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Group-by control (doc 33): collapse thousands of rows into buckets. */}
        <div className="glass-lite flex items-center gap-1 rounded-md px-2 py-1 text-xs">
          <Layers className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground mr-1">Group</span>
          {GROUP_BY_OPTIONS.map((o) => (
            <button
              key={o.id}
              onClick={() => setGroupBy(o.id)}
              className={cn(
                'rounded px-2 py-0.5 transition-colors',
                groupBy === o.id ? 'bg-primary/20 text-primary' : 'hover:bg-muted/40 text-muted-foreground',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>

        {reviewCount > 0 && (
          <Button
            variant={reviewView ? 'default' : 'glass'}
            size="sm"
            onClick={() => { setReviewView((v) => !v); clearSelection(); }}
            aria-pressed={reviewView}
            title="Lower-confidence matches, kept out of the main list"
          >
            {reviewView ? 'Back to active' : `Needs review (${reviewCount})`}
          </Button>
        )}
      </div>

      {/* Secondary status filter row - toggled by the "Filters" toolbar button. */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
          <span className="text-muted-foreground">Status:</span>
          {(['all', 'open', 'acknowledged', 'in_remediation', 'resolved', 'false_positive'] as const).map((s) => {
            const count = s === 'all' ? lane.length : lane.filter((f) => f.status === s).length;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={cn(
                  'glass-lite rounded-full px-3 py-1 capitalize transition-colors',
                  statusFilter === s ? 'bg-primary/20 text-primary border border-primary/40' : 'hover:bg-muted/40',
                )}
              >
                {s.replace(/_/g, ' ')} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Review lane note */}
      {reviewView && (
        <div className="glass-lite border border-sev-medium/30 rounded-md p-2.5 mb-3 text-xs text-muted-foreground flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-sev-medium shrink-0 mt-0.5" />
          <span>
            <strong className="text-foreground/90">Needs review.</strong> These matched a pattern but
            without a supporting column header or strong column density, so they are kept out of the
            main list. Open one to see the evidence, then confirm or mark it false-positive.
          </span>
        </div>
      )}

      {/* Bulk-triage bar (doc 33 #4) - acts on the current selection. */}
      {selectedIds.size > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-3d rounded-lg px-3 py-2 mb-3 flex flex-wrap items-center gap-2 border border-primary/30"
        >
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex flex-wrap gap-1.5 ml-auto">
            <Button variant="glass" size="sm" onClick={() => void applyBulkStatusChange('in_progress', 'Acknowledged by operator (bulk)')}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Acknowledge
            </Button>
            <Button variant="glass" size="sm" onClick={() => void applyBulkStatusChange('resolved', 'Resolved by operator (bulk)')}>
              <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Resolve
            </Button>
            <Button variant="glass" size="sm" onClick={() => void applyBulkStatusChange('false_positive', 'Marked false-positive by operator (bulk)')}>
              <EyeOff className="h-3.5 w-3.5 mr-1" /> False positive
            </Button>
            <Button variant="glass" size="sm" onClick={() => exportFindingsAsCsv(visible.filter((f) => selectedIds.has(f.id)))}>
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
            <Button variant="glass" size="sm" onClick={clearSelection}>
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        </motion.div>
      )}

      {/* Showing X of Y - so the rendered count, the tabs, and the lane total
          can never silently disagree (the founder's count-mismatch fix). */}
      {!loading && lane.length > 0 && (
        <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5 px-1">
          <span>
            Showing {visible.length.toLocaleString('en-IN')} of {lane.length.toLocaleString('en-IN')}{' '}
            {reviewView ? 'in review' : 'confirmed'}
            {visible.length !== lane.length ? ' (filtered)' : ''}
            {groupBy !== 'none' && visible.length > 0 ? ` · ${groups.length} ${GROUP_NOUN[groupBy]}` : ''}
          </span>
          {visible.length !== lane.length && (
            <button
              onClick={() => { setFilter('all'); setStatusFilter('all'); setSearch(''); }}
              className="text-primary hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Findings list - grouped + virtualized + risk-ranked (doc 33). */}
      {!loading && lane.length > 0 && (
        <GlassCard className="p-0 overflow-hidden">
          {visible.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <div className="font-medium text-foreground/90">No findings match your filters</div>
              <div className="mt-1">Clear the severity, status, or search filters to see all {lane.length} {reviewView ? 'in review' : 'confirmed'}.</div>
            </div>
          ) : (
            <VirtualList<RenderItem>
              items={renderItems}
              height={listViewportH}
              itemHeight={(i) => (renderItems[i].kind === 'group' ? GROUP_ROW_H : FINDING_ROW_H)}
              getKey={(it) => (it.kind === 'group' ? `g:${it.group.key}` : `f:${it.finding.id}`)}
              renderRow={(it) =>
                it.kind === 'group' ? (
                  <GroupHeaderRow
                    group={it.group}
                    collapsed={collapsedGroups.has(it.group.key)}
                    allSelected={it.group.findings.length > 0 && it.group.findings.every((f) => selectedIds.has(f.id))}
                    onToggleCollapse={() => toggleCollapse(it.group.key)}
                    onToggleSelect={() => toggleSelectGroup(it.group)}
                  />
                ) : (
                  <FindingListRow
                    finding={it.finding}
                    grouped={groupBy !== 'none'}
                    selected={selectedIds.has(it.finding.id)}
                    pending={pendingMutations.has(it.finding.id)}
                    onToggleSelect={() => toggleSelect(it.finding.id)}
                    onOpen={() => setSelected(it.finding)}
                  />
                )
              }
            />
          )}
        </GlassCard>
      )}

      {/* P1 OBS-2.alt (2026-05-26) - visible pagination footer.
          Shows on every page (even page 1 with no Next) so the
          page-size selector is always accessible. */}
      {!loading && (findings.length > 0 || pageIndex > 0) && (
        <div className="flex items-center justify-between gap-3 mt-3 px-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Page size:</span>
            <select
              className="bg-card border border-border/40 rounded px-2 py-1 text-xs"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) as (typeof PAGE_SIZE_CHOICES)[number])}
              aria-label="Findings page size"
            >
              {PAGE_SIZE_CHOICES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="ml-3">
              Loaded {findings.length} this page · page {pageIndex + 1}
              {nextCursor === null && pageIndex === 0 && ' (single page)'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="glass"
              size="sm"
              onClick={goPrevPage}
              disabled={pageIndex === 0}
              aria-label="Previous page"
            >
              ← Previous
            </Button>
            <Button
              variant="glass"
              size="sm"
              onClick={goNextPage}
              disabled={nextCursor === null}
              aria-label="Next page"
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {/* Empty state - lane-aware (review vs confirmed). */}
      {!loading && lane.length === 0 && !error && (
        <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
          <ShieldCheck className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <div className="font-medium text-foreground/90">
            {reviewView ? 'Nothing needs review' : findings.length > 0 ? 'No confirmed findings' : 'No findings yet'}
          </div>
          <div className="mt-1">
            {reviewView
              ? 'Every match was confidently classified.'
              : findings.length > 0
                ? `${reviewCount} match${reviewCount === 1 ? '' : 'es'} need a review - open the "Needs review" lane above.`
                : 'Run a discovery scan to start surfacing PII exposures.'}
          </div>
        </div>
      )}

      {/* Drill-in dialog - Why-Detected drill-down */}
      <Dialog open={!!selected} onOpenChange={(b) => !b && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          {selected && (
            <DetailErrorBoundary key={selected.id} fallback={<FindingDetailCore finding={selected} />}>
              <FindingDetail
                finding={selected}
                pending={pendingMutations.has(selected.id)}
                onAcknowledge={() => applyStatusChange(selected, 'in_progress', 'Acknowledged by operator')}
                onSuppress={() => applyStatusChange(selected, 'false_positive', 'Marked false-positive by operator')}
                onResolve={() => applyStatusChange(selected, 'resolved', 'Resolved by operator')}
                onDone={() => { void refresh(); setSelected(null); }}
              />
            </DetailErrorBoundary>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function SeverityDot({ severity }: { severity: Severity }) {
  return (
    <div className={cn(
      'h-2 w-2 rounded-full',
      severity === 'critical' && 'bg-sev-critical animate-pulse glow-critical',
      severity === 'high' && 'bg-sev-high glow-high',
      severity === 'medium' && 'bg-sev-medium',
      severity === 'low' && 'bg-sev-low',
    )} />
  );
}

const RISK_PILL_CLS: Record<RiskBand, string> = {
  critical: 'bg-sev-critical/15 text-sev-critical',
  high: 'bg-sev-high/15 text-sev-high',
  medium: 'bg-sev-medium/15 text-sev-medium',
  low: 'bg-sev-low/15 text-sev-low',
};

/** The unified 0-100 risk score, shown as a band-coloured pill. The single
 *  most important number on a row - everything is ranked by it (doc 33 #2). */
function RiskPill({ score, band, title }: { score: number; band: RiskBand; title?: string }) {
  return (
    <span
      className={cn('inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-semibold tabular w-9 shrink-0', RISK_PILL_CLS[band])}
      title={title ?? `Risk ${score}/100`}
    >
      {score}
    </span>
  );
}

/** Accessible select toggle used by rows + group headers for bulk triage. */
function SelectBox({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
    >
      {checked ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4" />}
    </button>
  );
}

/** A collapsible group header: select-all, expand/collapse, worst risk, count,
 *  aggregate records. Sized to GROUP_ROW_H for the windowed list. */
export function GroupHeaderRow({
  group,
  collapsed,
  allSelected,
  onToggleCollapse,
  onToggleSelect,
}: {
  group: FindingGroup;
  collapsed: boolean;
  allSelected: boolean;
  onToggleCollapse: () => void;
  onToggleSelect: () => void;
}) {
  return (
    <div className="h-12 w-full flex items-center gap-2 px-3 bg-muted/30 border-b border-border/40">
      <SelectBox checked={allSelected} onChange={onToggleSelect} label={`Select all in ${group.label}`} />
      <button
        type="button"
        onClick={onToggleCollapse}
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={collapsed ? 'Expand group' : 'Collapse group'}
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      <RiskPill score={group.topScore} band={group.band} title={`Worst risk in group: ${group.topScore}/100`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground/95 truncate flex items-center gap-2">
          <span className="truncate">{group.label}</span>
          <Badge variant="glass" className="text-[9px] shrink-0">{group.findings.length}</Badge>
        </div>
      </div>
      {group.records > 0 && (
        <div className="hidden sm:block text-xs text-muted-foreground tabular shrink-0">
          {group.records.toLocaleString('en-IN')} records
        </div>
      )}
    </div>
  );
}

/** A single finding row in the windowed list. Sized to FINDING_ROW_H. */
export function FindingListRow({
  finding,
  selected,
  pending,
  grouped,
  onToggleSelect,
  onOpen,
}: {
  finding: ScoredFinding;
  selected: boolean;
  pending: boolean;
  grouped: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
}) {
  const f = finding;
  return (
    <div
      onClick={onOpen}
      className={cn(
        'h-[70px] w-full flex items-center gap-3 px-3 text-left text-sm border-b border-border/30 hover:bg-muted/40 transition-colors cursor-pointer group',
        grouped && 'pl-9',
        selected ? 'bg-primary/5' : f.severity === 'critical' && 'bg-sev-critical/5',
      )}
    >
      <SelectBox checked={selected} onChange={onToggleSelect} label={`Select ${f.type} in ${f.asset}`} />
      <RiskPill score={f.risk.score} band={f.risk.band} />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-xs text-foreground/95 truncate flex items-center gap-1.5">
          {f.isBreach && (
            <Badge variant="critical" className="text-[8px] gap-0.5 shrink-0 inline-flex items-center">
              <ShieldAlert className="h-2.5 w-2.5" /> BREACH
            </Badge>
          )}
          <span className="truncate">{f.asset}</span>
          {f.legitimacy === 'orphan_no_owner' && (
            <Badge variant="warning" className="text-[8px] shrink-0">orphan</Badge>
          )}
          {f.tier === 'review' && (
            <Badge variant="warning" className="text-[8px] shrink-0">review</Badge>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 truncate">
          <span className="capitalize shrink-0">{f.type.replace(/_/g, ' ')}</span>
          {/* Distinguisher for near-identical breach rows: the incident ref. */}
          {f.incidentRef && (
            <>
              <span className="opacity-50">·</span>
              <code className="text-[10px] text-sev-high shrink-0">{f.incidentRef}</code>
            </>
          )}
          <span className="opacity-50">·</span>
          <span className="capitalize shrink-0">{f.governanceCategory.replace(/_/g, ' ')}</span>
          {f.pii.slice(0, 4).map((p) => (
            <Badge key={p} variant="glass" className="text-[8px] shrink-0">{p}</Badge>
          ))}
          {f.pii.length > 4 && <span className="opacity-60 text-[10px] shrink-0">+{f.pii.length - 4}</span>}
          {/* Masked sample of the actual matched value (privacy-safe). */}
          {f.maskedSamples[0] && (
            <code className="text-[9px] text-emerald-300/90 bg-emerald-500/10 rounded px-1 py-0.5 font-mono shrink-0">
              {f.maskedSamples[0]}
            </code>
          )}
        </div>
      </div>
      <div className="hidden sm:block w-24 text-right tabular text-xs shrink-0">
        {f.count !== null ? (
          <>
            <div className="text-foreground/90">{f.count.toLocaleString('en-IN')}</div>
            <div className="text-[10px] text-muted-foreground">records</div>
          </>
        ) : f.matchedCount !== null && f.samplesCount ? (
          <>
            <div className="text-foreground/90">{f.matchedCount}/{f.samplesCount}</div>
            <div className="text-[10px] text-muted-foreground">matched</div>
          </>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </div>
      <div className="hidden md:block w-32 text-xs shrink-0 min-w-0">
        <div className="flex items-center gap-1.5 text-foreground/90 truncate">
          <User className="h-3 w-3 opacity-60 shrink-0" />
          {f.owner === 'unassigned' ? <span className="text-muted-foreground italic">unassigned</span> : <span className="truncate">{f.owner}</span>}
        </div>
        <StatusBadge status={f.status} />
      </div>
      <div className="w-16 text-right text-xs text-muted-foreground flex items-center justify-end gap-1 shrink-0">
        {pending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <>
            <Clock className="h-3 w-3" />
            {f.detected}
          </>
        )}
        <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const map: Record<Status, { label: string; variant: 'glass' | 'success' | 'warning' | 'critical' | 'info' }> = {
    open: { label: 'Open', variant: 'critical' },
    acknowledged: { label: 'Ack', variant: 'warning' },
    in_remediation: { label: 'Fixing', variant: 'info' },
    resolved: { label: 'Resolved', variant: 'success' },
    false_positive: { label: 'False+', variant: 'glass' },
  };
  return <Badge variant={map[status].variant} className="text-[9px] mt-0.5">{map[status].label}</Badge>;
}

/**
 * "Why this is flagged" - the evidence the DPO verifies once per asset instead
 * of checking every row. Built entirely from data already on the finding: the
 * validation that passed, where it lives, how densely it matched, and a masked
 * sample. This is the answer to "how do I know this is not a false positive".
 */
function FindingEvidence({ finding }: { finding: Finding }) {
  const strength =
    finding.matchedCount !== null && finding.samplesCount
      ? `${finding.matchedCount} of ${finding.samplesCount} sampled values matched`
      : finding.count !== null
        ? `${finding.count.toLocaleString('en-IN')} records`
        : null;
  const rows: Array<{ k: string; v: ReactNode }> = [
    { k: 'What', v: <span className="capitalize">{finding.pii.join(', ') || finding.type.replace(/_/g, ' ')}</span> },
    {
      k: 'DPDPA class',
      v: (
        <span className="capitalize">
          {finding.governanceCategory.replace(/_/g, ' ')}{' '}
          <span className="text-muted-foreground">({finding.sensitivity} sensitivity)</span>
        </span>
      ),
    },
    { k: 'How we validated', v: validationNote(finding.pii) },
    { k: 'Where', v: <code className="font-mono text-[11px] break-all">{finding.asset}</code> },
  ];
  if (strength) {
    rows.push({ k: 'How strong', v: `${strength} (${Math.round((finding.confidence || 0) * 100)}% confidence)` });
  }
  // The scanner's own reason for the verdict. Rendered only when present:
  // findings scanned before 2026-07-21 carry none, and inventing a placeholder
  // would imply the scanner had a reason it never recorded.
  if (finding.rationale) {
    rows.push({
      k: 'Why this verdict',
      v: <span className="text-[12px] leading-relaxed text-foreground/90">{finding.rationale}</span>,
    });
  }
  if (finding.maskedSamples.length > 0) {
    rows.push({
      k: 'Proof (masked)',
      v: (
        <span className="flex flex-wrap gap-1.5">
          {finding.maskedSamples.map((s) => (
            <code key={s} className="text-[11px] text-emerald-300/90 bg-emerald-500/10 rounded px-1.5 py-0.5 font-mono">
              {s}
            </code>
          ))}
        </span>
      ),
    });
  }
  return (
    <div className="glass-lite rounded-md p-3 border border-primary/25 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-primary flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5" /> Why this is flagged
      </div>
      <dl className="grid grid-cols-[112px_1fr] gap-x-3 gap-y-1.5 text-xs items-start">
        {rows.map((r) => (
          <Fragment key={r.k}>
            <dt className="text-muted-foreground">{r.k}</dt>
            <dd className="text-foreground/90 min-w-0">{r.v}</dd>
          </Fragment>
        ))}
      </dl>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Verify this basis once for the asset - the validation, where it lives, and how densely it
        matches. The full value never leaves the scanner; only the masked sample is stored.
      </p>
    </div>
  );
}

/**
 * Trust verdict (doc 33 follow-up) - the FIRST thing the operator should see:
 * a plain answer to "is this real PII, or noise?" plus the one decision that
 * matters at this point - dismiss it as a false positive. The positive case
 * needs no button: you just go act on it.
 */
function TrustVerdict({ finding, onDismiss, pending }: { finding: Finding; onDismiss: () => void; pending: boolean }) {
  const confirmed = finding.tier !== 'review';
  const strength =
    finding.matchedCount !== null && finding.samplesCount
      ? `${finding.matchedCount} of ${finding.samplesCount} sampled values matched`
      : finding.count !== null
        ? `${finding.count.toLocaleString('en-IN')} records`
        : null;
  return (
    <div
      className={cn(
        'rounded-lg border p-3 flex flex-wrap items-center gap-3',
        confirmed ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5',
      )}
    >
      {confirmed ? (
        <ShieldCheck className="h-5 w-5 text-success shrink-0" />
      ) : (
        <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className={cn('text-sm font-semibold', confirmed ? 'text-success' : 'text-warning')}>
          {confirmed ? 'Confirmed - this is real PII' : 'Needs your review'}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {confirmed
            ? `${validationNote(finding.pii)}${strength ? ` · ${strength}` : ''} · ${Math.round((finding.confidence || 0) * 100)}% confidence`
            : 'Matched a pattern but without a supporting column name or strong density. Confirm it is real, or dismiss it as not-PII below.'}
        </div>
      </div>
      <Button variant="glass" size="sm" onClick={onDismiss} disabled={pending} title="Mark this as a false positive - it trains the scanner to stop flagging it">
        <EyeOff className="h-3.5 w-3.5 mr-1" /> Not PII - dismiss
      </Button>
    </div>
  );
}

/**
 * MALWARE verdict (threat-signature match) - shown INSTEAD of the PII
 * TrustVerdict for malicious_file_detected findings (or any finding whose
 * detection includes yara/hash). A red "THREAT DETECTED" banner + the
 * forensic facts (family / YARA rule / SHA-256 / path) and a highlighted
 * co-location callout: malware sitting on a store of aadhaar / cards is an
 * active exfiltration risk, which raises the finding's priority.
 */
function MalwareVerdict({ finding }: { finding: Finding }) {
  const method =
    finding.detection && finding.detection.length > 0
      ? finding.detection
      : [finding.yaraRule ? 'yara' : null, finding.sha256 ? 'hash' : null]
          .filter(Boolean)
          .join('+') || 'signature';
  const rows: Array<{ k: string; v: ReactNode }> = [];
  if (finding.malwareFamily)
    rows.push({ k: 'Malware family', v: <span className="font-medium text-sev-critical">{finding.malwareFamily}</span> });
  if (finding.yaraRule)
    rows.push({ k: 'YARA rule', v: <code className="font-mono text-[11px]">{finding.yaraRule}</code> });
  if (finding.sha256)
    rows.push({ k: 'SHA-256', v: <code className="font-mono text-[11px] break-all">{finding.sha256}</code> });
  if (finding.filePath)
    rows.push({ k: 'File path', v: <code className="font-mono text-[11px] break-all">{finding.filePath}</code> });
  return (
    <div className="space-y-3">
      {/* Red THREAT DETECTED banner */}
      <div className="rounded-lg border border-sev-critical/50 bg-sev-critical/10 p-3 flex flex-wrap items-center gap-3">
        <ShieldAlert className="h-5 w-5 text-sev-critical shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-sev-critical flex items-center gap-2">
            THREAT DETECTED
            <Badge variant="critical" className="text-[9px]">malware</Badge>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Confirmed: known-malware signature match · detected via{' '}
            {finding.yaraRule ? 'YARA rule' : 'threat-intel signature'}
            {finding.sha256 ? ' + threat-intel SHA-256' : ''}{' '}
            <span className="opacity-70">({method})</span>
          </div>
        </div>
      </div>

      {/* Forensic facts - family / YARA rule / SHA-256 / path. */}
      {rows.length > 0 && (
        <div className="glass-lite rounded-md p-3 border border-sev-critical/25">
          <dl className="grid grid-cols-[104px_1fr] gap-x-3 gap-y-1.5 text-xs items-start">
            {rows.map((r) => (
              <Fragment key={r.k}>
                <dt className="text-muted-foreground">{r.k}</dt>
                <dd className="text-foreground/90 min-w-0">{r.v}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      )}

      {/* Co-location callout - the exfiltration-risk multiplier. */}
      {finding.coLocatedSensitive && (
        <div className="flex items-start gap-2 rounded-md border border-sev-high/50 bg-sev-high/10 p-2.5 text-xs">
          <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
          <span>
            <strong className="text-sev-high">
              Co-located with sensitive data: {finding.coLocatedSensitive}.
            </strong>{' '}
            Malware sitting on a store of {finding.coLocatedSensitive} is an active exfiltration risk -
            this raises the finding's priority above a malware match on a low-value asset.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * What to do next - turns the (text) playbook into clickable next steps that
 * navigate to exactly where each action is performed. These are real: lineage,
 * the asset/owner page, the scan launcher, and an evidence export. No fake
 * buttons - if a capability isn't here yet, it isn't shown.
 */
function NextActions({ finding, onDone }: { finding: Finding; onDone?: () => void }) {
  const navigate = useNavigate();
  const [assignOpen, setAssignOpen] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const goInventory = (): void => {
    // The catalog searches by display name (e.g. "public.customers"); a column
    // finding's asset is a full URI, so stash just the table segment.
    const assetKey = finding.asset.split('/').pop() || finding.asset;
    try { sessionStorage.setItem('discovery.inventorySearch', assetKey); } catch { /* ignore */ }
    navigate({ to: '/dspm/inventory' });
  };

  // One-click remediation loop: an unowned orphan-PII finding can be fixed in
  // place - assign an owner, the server verifies (re-runs the detector) and
  // auto-resolves the finding.
  const canOneClickAssign = finding.type === 'orphan_pii_no_owner' && finding.owner === 'unassigned';

  async function submitAssign(): Promise<void> {
    const email = ownerEmail.trim();
    if (!email) { setError('Enter an owner email'); return; }
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await remediateFinding(finding.id, 'assign_owner', { ownerEmail: email });
      setResult(r.verified ? 'Owner assigned - finding resolved.' : 'Owner assigned - it will re-check on the next scan.');
      setAssignOpen(false);
      if (r.verified) window.setTimeout(() => onDone?.(), 800);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Remediation failed');
    } finally {
      setBusy(false);
    }
  }

  const actions: Array<{ id: string; label: string; icon: typeof GitBranchIcon; onClick: () => void }> = [
    { id: 'lineage', label: 'See where this data flows', icon: GitBranchIcon, onClick: () => navigate({ to: '/dspm/lineage' }) },
    canOneClickAssign
      ? { id: 'owner', label: 'Assign owner & resolve', icon: DatabaseIcon, onClick: () => { setAssignOpen((v) => !v); setError(null); } }
      : { id: 'owner', label: finding.owner === 'unassigned' ? 'Assign a data owner' : 'View asset & owner', icon: DatabaseIcon, onClick: goInventory },
    { id: 'scan', label: 'Run a deeper / targeted scan', icon: RadarIcon, onClick: () => navigate({ to: '/dspm/scans' }) },
    { id: 'export', label: 'Export evidence (CSV)', icon: DownloadIcon, onClick: () => exportFindingsAsCsv([finding]) },
  ];
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">What to do next</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              type="button"
              onClick={a.onClick}
              className="group flex items-center gap-2 rounded-md border border-border/40 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40 hover:border-primary/40"
            >
              <Icon className="h-4 w-4 text-primary shrink-0" />
              <span className="flex-1">{a.label}</span>
              <ChevronRight className="h-3.5 w-3.5 opacity-30 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>
          );
        })}
      </div>
      {assignOpen && canOneClickAssign && (
        <div className="mt-2 flex flex-col gap-2 rounded-md border border-primary/30 bg-muted/20 p-2 sm:flex-row sm:items-center">
          <input
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submitAssign(); }}
            placeholder="owner@company.com"
            aria-label="Data owner email"
            className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void submitAssign()}
            disabled={busy}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? 'Assigning…' : 'Assign & resolve'}
          </button>
        </div>
      )}
      {result && <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">{result}</p>}
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      <p className="text-[11px] text-muted-foreground mt-2">
        The steps above explain <em>why</em>; these take you to <em>where</em> to do each one.
      </p>
    </div>
  );
}

function FindingDetail({
  finding,
  pending,
  onAcknowledge,
  onSuppress,
  onResolve,
  onDone,
}: {
  finding: Finding;
  pending: boolean;
  onAcknowledge: () => void;
  onSuppress: () => void;
  onResolve: () => void;
  onDone?: () => void;
}) {
  return (
    <div className="space-y-4">
      <DialogHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <DialogTitle className="flex items-center gap-2 capitalize">
              <SeverityDot severity={finding.severity} />
              {finding.type.replace(/_/g, ' ')}
            </DialogTitle>
            <DialogDescription>
              <code className="font-mono text-xs">{finding.asset}</code>
            </DialogDescription>
          </div>
          <Badge variant={finding.severity}>{finding.severity}</Badge>
        </div>
      </DialogHeader>

      <Separator />

      {/* Verdict: MALWARE threat presentation for threat-signature matches,
          otherwise the PII trust verdict + "not PII, dismiss" decision. */}
      {finding.isMalware ? (
        <MalwareVerdict finding={finding} />
      ) : (
        <TrustVerdict finding={finding} onDismiss={onSuppress} pending={pending} />
      )}

      {/* Finding / tracking ID - jargon softened + explained for newcomers. */}
      <div className="glass-lite rounded-md p-3 flex items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            {finding.isBreach ? 'Tracking ID' : 'Finding ID'}
            {finding.isBreach && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-3 w-3 opacity-60 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    The same ID appears in the database error, the audit log, and your SIEM ticket, so you can trace this exact exposure end to end.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <code className="font-mono text-sm">{finding.id}</code>
        </div>
      </div>

      {/* PII-oriented evidence + stat grid - only for PII findings. Malware
          findings show their own family / YARA / SHA-256 rows in MalwareVerdict. */}
      {!finding.isMalware && (
        <>
          <FindingEvidence finding={finding} />

          <div className="grid grid-cols-2 gap-3">
            <Stat
              label={finding.count !== null ? 'Records affected' : 'Sampled values matched'}
              value={
                finding.count !== null
                  ? finding.count.toLocaleString('en-IN')
                  : finding.matchedCount !== null && finding.samplesCount
                    ? `${finding.matchedCount} of ${finding.samplesCount}`
                    : '-'
              }
            />
            <Stat label="PII categories" value={finding.pii.join(', ')} />
            <Stat label="Confidence" value={`${Math.round(finding.confidence * 100)}%`} />
            <Stat label="Detected" value={finding.detected} />
          </div>
        </>
      )}

      {finding.isBreach && <BreachForensics finding={finding} />}

      <Separator />

      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Recommended playbook</div>
        <div className="space-y-2">
          {RECOMMENDED_ACTIONS[finding.type as keyof typeof RECOMMENDED_ACTIONS]?.map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <span className="h-5 w-5 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-semibold shrink-0">{i + 1}</span>
              <span>{a}</span>
            </div>
          )) ?? <p className="text-sm text-muted-foreground">No playbook recommendation yet.</p>}
        </div>
      </div>

      <Separator />

      {/* The clickable counterpart to the playbook above. */}
      <NextActions finding={finding} onDone={onDone} />

      <Separator />

      <DetailErrorBoundary
        fallback={<div className="text-xs text-muted-foreground">Lineage unavailable for this finding.</div>}
      >
        <FindingLineageSection findingId={finding.id} />
      </DetailErrorBoundary>

      <Separator />

      <div className="flex items-center gap-2">
        <span className="text-[11px] text-muted-foreground mr-1">Update status:</span>
        <Button variant="glass" size="sm" onClick={onAcknowledge} disabled={pending}>
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          Acknowledge
        </Button>
        <Button size="sm" onClick={onResolve} disabled={pending}>
          Mark resolved
        </Button>
        <span className="ml-auto text-[11px] text-muted-foreground">Not real PII? Use “Not PII - dismiss” at the top.</span>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm tabular mt-0.5">{value}</div>
    </div>
  );
}

/**
 * Breach forensics panel - shown for breach_data_located findings. Surfaces the
 * P3 forensic locate (where the breached data class lives + who owns it + the
 * system/IP + record count + which columns), tagged to the Core breach.
 */
function BreachForensics({ finding }: { finding: Finding }) {
  const orphan = finding.legitimacy === 'orphan_no_owner';
  const recordsText = finding.count === null ? 'records' : `${finding.count.toLocaleString('en-IN')} record${finding.count === 1 ? '' : 's'}`;
  const classesText = finding.pii.length ? finding.pii.join(', ') : 'personal data';
  const ownerSentence = orphan
    ? 'No data owner is assigned to this asset - orphaned personal data is an exposure risk and should be assigned an owner.'
    : finding.owner && finding.owner !== 'unassigned'
      ? `The data owner on record is ${finding.owner}.`
      : 'No data owner is recorded.';
  return (
    <div className="glass-lite rounded-md p-4 border border-sev-critical/30 space-y-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-sev-high">
        <ShieldAlert className="h-4 w-4" /> Breach forensics
        {finding.incidentRef && (
          <Badge variant="critical" className="ml-auto text-[9px]">{finding.incidentRef}</Badge>
        )}
      </div>

      {/* Plain-language summary - written for a DPO / management reader. */}
      <p className="text-sm leading-relaxed text-foreground/90">
        During breach <strong>{finding.incidentRef ?? 'investigation'}</strong>, a scan of your
        connected source <strong>{finding.sourceSystem ?? finding.asset}</strong> located{' '}
        <strong>{recordsText}</strong> containing <strong>{classesText}</strong>.{' '}
        {ownerSentence} These forensic facts were sent to the Consent &amp; Compliance module to
        drive the regulator notification.
      </p>

      {/* At-a-glance facts in plain labels. */}
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Where the data lives" value={finding.sourceSystem ?? '-'} />
        <Stat label="System / location" value={finding.systemIp ?? '-'} />
        <Stat label="Records affected" value={finding.count === null ? '-' : finding.count.toLocaleString('en-IN')} />
        <Stat label="Data owner" value={orphan ? '⚠ None (orphaned)' : finding.owner === 'unassigned' ? '-' : finding.owner} />
      </div>

      {/* What kinds of personal data, by column. */}
      {finding.matchedColumns.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Personal data found (by column)
          </div>
          <div className="space-y-1">
            {finding.matchedColumns.map((c) => (
              <div key={c.columnName} className="flex items-center gap-2 text-xs">
                <code className="glass-lite px-1.5 py-0.5 rounded text-[10px]">{c.columnName}</code>
                <div className="flex flex-wrap gap-1">
                  {c.matchedClasses.map((cl) => (
                    <Badge key={cl} variant="warning" className="text-[8px]">{cl}</Badge>
                  ))}
                </div>
                {c.matchesFound > 1 && (
                  <span className="ml-auto tabular text-[10px] text-muted-foreground">
                    {c.matchesFound.toLocaleString('en-IN')} matches
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deep-scan forensic sweep - only present when a DEEP scan ran. Two
          real, additive signals beyond the locate: toxic combinations + who
          actually accessed the asset. */}
      {(finding.toxicCombinations.length > 0 || finding.recentAccess.length > 0 || finding.activityObserved) && (
        <div className="rounded-md border border-sev-high/30 bg-sev-high/5 p-3 space-y-3">
          <div className="text-[10px] uppercase tracking-wider text-sev-high flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5" /> Forensic deep-scan sweep
          </div>

          {finding.toxicCombinations.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                Toxic combinations on this asset
              </div>
              <div className="flex flex-wrap gap-1.5">
                {finding.toxicCombinations.map((combo, i) => (
                  <code key={i} className="text-[10px] bg-sev-critical/10 text-sev-critical rounded px-1.5 py-0.5">
                    {combo.join(' + ')}
                  </code>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Identity + financial (or health) data co-located - elevated DPDPA data-minimisation risk.
              </p>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Who accessed this asset (last 30 days)
            </div>
            {finding.recentAccess.length > 0 ? (
              <div className="space-y-1">
                {finding.recentAccess.map((r) => (
                  <div key={r.actor} className="flex items-center gap-2 text-xs">
                    <User className="h-3 w-3 opacity-60" />
                    <span className="text-foreground/90">{r.actor}</span>
                    <span className="ml-auto tabular text-[10px] text-muted-foreground">
                      {r.events} event{r.events === 1 ? '' : 's'} · last {r.lastAccessedAt.slice(0, 10)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-muted-foreground">
                {finding.activityObserved
                  ? 'No actor activity in the window.'
                  : 'No activity capture configured for this source (deploy the Activity collector to populate who-accessed forensics).'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Risk callout for orphaned personal data. */}
      {orphan && (
        <div className="flex items-start gap-2 rounded-md border border-sev-high/40 bg-sev-high/5 p-2 text-xs">
          <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
          <span>
            <strong>Orphaned data -</strong> no one is accountable for this personal data. Assign a
            data owner and confirm whether it falls within the breach scope.
          </span>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground leading-relaxed">
        Located by a Core-triggered breach scan and reported back to FOCTTA Command Center
        (<code className="text-[9px]">dspm.breach_response</code>) for ROPA correlation and the
        72-hour regulator notification packet.
      </p>
    </div>
  );
}

/**
 * F-2.8 lineage drill-down (T1.3). Fetches the full chain for this
 * finding - asset → table → column, linked DDR decisions, linked breach
 * incidents, and recent activity history - when the drawer opens. Each
 * section degrades to "none" gracefully (the server returns empty arrays
 * when a source table/column isn't present rather than 500ing).
 */
function FindingLineageSection({ findingId }: { findingId: string }) {
  const [lineage, setLineage] = useState<FindingLineage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLineage(null);
    getFindingLineage(findingId)
      .then((l) => { if (!cancelled) setLineage(l); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'lineage load failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [findingId]);

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <GitBranch className="h-3.5 w-3.5" /> Lineage &amp; cross-pillar links
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Tracing lineage…
        </div>
      )}
      {error && !loading && (
        <div className="text-xs text-destructive">{error}</div>
      )}

      {lineage && !loading && (
        <div className="space-y-3 text-xs">
          {/* Asset chain */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Asset chain</div>
            {lineage.assetChain.length === 0 ? (
              <span className="text-muted-foreground italic">no asset linked</span>
            ) : (
              <div className="flex flex-wrap items-center gap-1">
                {lineage.assetChain.map((a, i) => (
                  <span key={a.id} className="flex items-center gap-1">
                    <code className="glass-lite px-1.5 py-0.5 rounded text-[10px]">
                      <span className="text-muted-foreground">{a.kind}:</span> {a.label}
                    </code>
                    {i < lineage.assetChain.length - 1 && <ChevronRight className="h-3 w-3 opacity-40" />}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Linked DDR decisions */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> Linked Detection decisions ({lineage.linkedDecisions.length})
            </div>
            {lineage.linkedDecisions.length === 0 ? (
              <span className="text-muted-foreground italic">none</span>
            ) : (
              <div className="space-y-1">
                {lineage.linkedDecisions.slice(0, 6).map((d) => (
                  <div key={d.id} className="flex items-center gap-2">
                    <Badge variant={d.decision === 'block' ? 'critical' : d.decision === 'alert' ? 'warning' : 'glass'} className="text-[9px]">
                      {d.decision}
                    </Badge>
                    {d.requestId && <code className="text-[10px] text-muted-foreground">{d.requestId}</code>}
                    <span className="text-[10px] text-muted-foreground ml-auto">{timeAgo(d.decidedAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linked breach incidents */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> Linked breach incidents ({lineage.linkedBreaches.length})
            </div>
            {lineage.linkedBreaches.length === 0 ? (
              <span className="text-muted-foreground italic">none</span>
            ) : (
              <div className="space-y-1">
                {lineage.linkedBreaches.slice(0, 6).map((b) => (
                  <div key={b.id} className="flex items-center gap-2">
                    <Badge variant={b.severity === 'critical' || b.severity === 'high' ? 'critical' : 'warning'} className="text-[9px]">
                      {b.severity}
                    </Badge>
                    <span className="capitalize text-[10px]">{b.status.replace(/_/g, ' ')}</span>
                    {b.affectedPrincipalsCount !== null && (
                      <span className="text-[10px] text-muted-foreground">{b.affectedPrincipalsCount.toLocaleString('en-IN')} principals</span>
                    )}
                    <span className="text-[10px] text-muted-foreground ml-auto">{timeAgo(b.openedAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Activity history */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
              <ActivityIcon className="h-3 w-3" /> Recent history ({lineage.activityHistory.length})
            </div>
            {lineage.activityHistory.length === 0 ? (
              <span className="text-muted-foreground italic">none</span>
            ) : (
              <div className="space-y-1">
                {lineage.activityHistory.slice(0, 8).map((h, i) => (
                  <div key={`${h.ts}-${i}`} className="flex items-start gap-2">
                    <span className="text-[10px] text-muted-foreground tabular shrink-0">{timeAgo(h.ts)}</span>
                    <span className="text-[10px]">
                      <code className="text-muted-foreground">{h.eventType}</code> - {h.summary}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const RECOMMENDED_ACTIONS = {
  malicious_file_detected: [
    'Quarantine or isolate the affected file / host immediately - do not open or execute it.',
    'If sensitive data is co-located, treat this as a potential exfiltration path and scope the blast radius.',
    'Pull the file hash + YARA rule into your EDR / SIEM to sweep for the same signature elsewhere.',
    'Rotate any credentials or keys reachable from the affected host.',
    'Preserve forensic evidence (file, path, access logs) before remediation for the incident record.',
  ],
  breach_data_located: [
    'Confirm this asset is in the breach blast radius (cross-check the located records against the reported principals).',
    'If confirmed, scope containment on the source system and rotate any exposed credentials.',
    'Include this asset in the regulator notification packet (Core correlates via dspm.breach_response).',
    'For orphan data (no owner), assign an owner and review retention - orphaned PII is an exposure multiplier.',
    'Trigger the deep scan (egress / destination-IP) to establish whether data moved out.',
  ],
  aadhaar_in_non_vault: [
    'Migrate column to UIDAI Aadhaar Vault per UIDAI 2025 Circular.',
    'Tokenise downstream references; retain reference key only.',
    'Notify DPO + open DPIA assessment task.',
    'Rotate any pre-existing exports; verify with hash audit.',
  ],
  section_9_minor_no_consent: [
    'Block all reads at runtime (Section 9 Hard-Block engine activates automatically).',
    'Trigger guardian-consent verification flow with verifiable parental consent.',
    'Open DPIA + escalate to Legal Reviewer for 2-person approval.',
    'File draft DPB notification (auto-pre-filled by Detection module).',
  ],
  cross_border_no_scc: [
    'Activate Cross-Border Geofence engine; block transfer at SQL proxy.',
    'Generate Standard Contractual Clauses for the destination country (or block by policy).',
    'Run transfer-impact-assessment workflow; route to Legal Reviewer.',
  ],
  public_pii_exposed: [
    'Quarantine bucket / table; revoke public ACL.',
    'Generate breach incident draft (RBI 6h clock starts).',
    "Notify affected data principals - use Find a person's data to scope who is affected.",
  ],
  orphan_pii_no_owner: [
    'Assign data owner via Settings → Asset Ownership.',
    'Schedule retention review; flag for purge if past retention.',
  ],
  honey_pii_read: [
    'Initiate session quarantine immediately (read should never have happened).',
    'Open Triple-Redundancy investigation; pull statement-hash + actor history.',
    'Escalate via Banking 2P approval flow.',
  ],
  pan_in_logs: [
    'Configure log redaction at the application layer.',
    'Purge existing log entries containing PAN.',
    'Add rule to Detection: block writes containing PAN format to log streams.',
  ],
  email_pii_present: [
    'Confirm this column legitimately needs to store email; if not, mask or remove it.',
    'Apply column-level access control + encryption at rest; restrict to roles that need it.',
    'Map the column to a declared purpose; flag use beyond it (DPDPA purpose-limitation).',
  ],
  phone_pii_present: [
    'Confirm the phone number is required here; minimise if not (DPDPA data-minimisation).',
    'Restrict column access; mask in non-production and reporting copies.',
    'Map to a declared purpose and add to the ROPA inventory.',
  ],
  pan_in_non_vault: [
    'Tokenise the PAN at the application boundary; retain only a reference key.',
    'Restrict and audit access to the column; review recent reads.',
    'Notify the DPO and assess against DPDPA data-minimisation.',
  ],
  card_in_non_vault: [
    'Tokenise/vault the card number per PCI-DSS - never store the full PAN in a non-vault store.',
    'Mask all but the last 4 digits at the application boundary.',
    'Open a PCI-DSS remediation task and audit who accessed the column.',
  ],
  imei_present: [
    'Confirm the device identifier is needed; minimise or hash if not.',
    'Apply access controls - treat IMEI as personal data under DPDPA.',
  ],
  secret_exposed: [
    'Rotate the exposed credential IMMEDIATELY - assume it is compromised.',
    'Remove the secret from the data store; move it to a secret manager / vault.',
    'Audit who accessed this column and check for misuse.',
  ],
};
